import { ColumnDef } from "@tanstack/react-table"
import React from "react"
import { FormattedMessage } from "react-intl"
import CustomField from "../../../../../state/custom-fields/CustomField"
import { Messages } from "../../../../../state/helpers/IntlProps"
import { ObjectTypeConfig } from "../../../../../state/listing/generic-listing"
import {
    ColumnDefinition,
    ColumnDefinitionCategory,
    CurrencyColumnDefinition,
    DataType,
    LinkColumnDefinition,
    StringColumnDefinition,
    TableRowData,
    TableRowId,
    TotalType
} from "../../../../../state/listing/generic-listing/column-definitions"
import SearchTotals from "../../../../../state/search/SearchTotals"
import { CUSTOM_FIELD_TYPE } from "../../../../../utils/customField/constants"
import { getValueFromObject } from "../../../../../utils/object"
import GlobalHelper from "../../../../GlobalHelper"
import arrayCellMapper from "./cell-mappers/ArrayCellMapper"
import badgeCellMapper from "./cell-mappers/BadgeCellMapper"
import booleanCellMapper from "./cell-mappers/BooleanCellMapper"
import componentCellMapper from "./cell-mappers/ComponentCellMapper"
import contactCellMapper from "./cell-mappers/ContactCellMapper"
import currencyCellMapper from "./cell-mappers/CurrencyCellMapper"
import dateCellMapper from "./cell-mappers/DateCellMapper"
import fileCellMapper from "./cell-mappers/FileCellMapper"
import imageCellMapper from "./cell-mappers/ImageCellMapper"
import inventoryCellMapper from "./cell-mappers/InventoryCellMapper"
import linkCellMapper from "./cell-mappers/LinkCellMapper"
import optionsCellMapper from "./cell-mappers/OptionsCellMapper"
import pricingCellMapper from "./cell-mappers/PricingCellMapper"
import stringCellMapper from "./cell-mappers/StringCellMapper"
import currencyTotalMapper from "./total-mappers/CurrencyTotalMapper"
import stringTotalMapper from "./total-mappers/StringTotalMapper"

export const mapColumnDefinitionToTableColumn = (
    columnDefinition: ColumnDefinition,
    headerMessages: Messages,
    totals: SearchTotals | undefined,
    showTotal: boolean,
    customFieldHeaders?: Messages,
): ColumnDef<TableRowData> => {

    switch (columnDefinition.dataType) {
    case DataType.STRING:
        return {
            ...stringCellMapper(columnDefinition as StringColumnDefinition),
            ...mapColumnDefinitionGenerics(columnDefinition, headerMessages, totals, showTotal, customFieldHeaders)
        }
    case DataType.DATE:
        return {
            ...dateCellMapper(columnDefinition),
            ...mapColumnDefinitionGenerics(columnDefinition, headerMessages, totals, showTotal, customFieldHeaders),
        }
    case DataType.CURRENCY:
        return {
            ...currencyCellMapper(columnDefinition),
            ...mapColumnDefinitionGenerics(columnDefinition, headerMessages, totals, showTotal, customFieldHeaders),
        }
    case DataType.LINK:
        return {
            ...linkCellMapper(columnDefinition as LinkColumnDefinition),
            ...mapColumnDefinitionGenerics(columnDefinition, headerMessages, totals, showTotal, customFieldHeaders),
        }
    case DataType.BADGE:
        return {
            ...badgeCellMapper(columnDefinition),
            ...mapColumnDefinitionGenerics(columnDefinition, headerMessages, totals, showTotal, customFieldHeaders),
        }
    case DataType.CONTACT:
        return {
            ...contactCellMapper(columnDefinition),
            ...mapColumnDefinitionGenerics(columnDefinition, headerMessages, totals, showTotal, customFieldHeaders),
        }
    case DataType.ARRAY:
        return {
            ...arrayCellMapper(columnDefinition),
            ...mapColumnDefinitionGenerics(columnDefinition, headerMessages, totals, showTotal, customFieldHeaders),
        }
    case DataType.BOOLEAN:
        return {
            ...booleanCellMapper(),
            ...mapColumnDefinitionGenerics(columnDefinition, headerMessages, totals, showTotal, customFieldHeaders),
        }
    case DataType.OPTIONS:
        return {
            ...optionsCellMapper(columnDefinition),
            ...mapColumnDefinitionGenerics(columnDefinition, headerMessages, totals, showTotal, customFieldHeaders),
        }
    case DataType.IMAGE:
        return {
            ...imageCellMapper(),
            ...mapColumnDefinitionGenerics(columnDefinition, headerMessages, totals, showTotal, customFieldHeaders),
        }
    case DataType.PRICING:
        return {
            ...pricingCellMapper(columnDefinition),
            ...mapColumnDefinitionGenerics(columnDefinition, headerMessages, totals, showTotal, customFieldHeaders),
        }
    case DataType.INVENTORY:
        return {
            ...inventoryCellMapper(columnDefinition),
            ...mapColumnDefinitionGenerics(columnDefinition, headerMessages, totals, showTotal, customFieldHeaders),
        }
    case DataType.COMPONENT:
        return {
            ...componentCellMapper(columnDefinition),
            ...mapColumnDefinitionGenerics(columnDefinition, headerMessages, totals, showTotal, customFieldHeaders),
        }
    case DataType.LOCATION:
        if (GlobalHelper.canEditLocation()) {
            columnDefinition.dataType = DataType.LINK
            return {
                ...linkCellMapper(columnDefinition as LinkColumnDefinition),
                ...mapColumnDefinitionGenerics(columnDefinition, headerMessages, totals, showTotal, customFieldHeaders),
            }
        } else {
            columnDefinition.dataType = DataType.STRING
            return {
                ...stringCellMapper(columnDefinition as StringColumnDefinition),
                ...mapColumnDefinitionGenerics(columnDefinition, headerMessages, totals, showTotal, customFieldHeaders)
            }
        }
    case DataType.FILE:
        return {
            ...fileCellMapper(),
            ...mapColumnDefinitionGenerics(columnDefinition, headerMessages, totals, showTotal, customFieldHeaders),
        }
    default:
        throw new Error("Error: Invalid data type for column definition.")
    }
}

export const getColumnDefinitionsWithCustomFields = (config: ObjectTypeConfig, customFields: CustomField[]): ColumnDefinition[] => {
    return [
        ...config.columnDefinitions,
        ...mapCustomFieldsToColumnDefinition(customFields),
    ]
}

export const mapCustomFieldsToColumnDefinition = (customFields: CustomField[]): ColumnDefinition[] => {
    const columnDefinition: ColumnDefinition[] = []
    
    const getCustomFieldValue = (row: TableRowData, customFieldId: string)  => {
        return row.customValues?.find((customFieldVal) => customFieldVal.id === customFieldId)
    }
    
    customFields.forEach(customField => {
        switch (customField.getFieldType()) {
        case CUSTOM_FIELD_TYPE.TEXT:
        case CUSTOM_FIELD_TYPE.MULTI_LINE_TEXT:
        case CUSTOM_FIELD_TYPE.SINGLE_CHOICE:
            columnDefinition.push({
                id: customField.getId() as TableRowId,
                dataType: DataType.STRING,
                accessorFn: row => {
                    const customFieldVal = getCustomFieldValue(row, customField.getId())
                    return customFieldVal ? customFieldVal.value : "-"
                },
                categories: [ColumnDefinitionCategory.OTHER],
                sortable: true,
                sortId: "customValues.value",
                nestedSortFilters: {["customValues.id"]: customField.getId()}
            })
            break
        case CUSTOM_FIELD_TYPE.IMAGE:
            columnDefinition.push({
                id: customField.getId() as TableRowId,
                dataType: DataType.IMAGE,
                accessorFn: row => {
                    const customFieldVal = getCustomFieldValue(row, customField.getId())
                    if (customFieldVal && customFieldVal.value) {
                        const images = JSON.parse(customFieldVal.value) 
                        if (images?.imageIds.length) {
                            return images.imageIds[0] ?? ""
                        }
                    }
                    return ""
                },
                categories: [ColumnDefinitionCategory.OTHER],
                sortable: false, 
            })
            break
        case CUSTOM_FIELD_TYPE.DATE:
            columnDefinition.push({
                id: customField.getId() as TableRowId,
                dataType: DataType.DATE,
                accessorFn: row => {
                    const customFieldVal = getCustomFieldValue(row, customField.getId())
                    return customFieldVal ? customFieldVal.valueDate : ""
                },
                categories: [ColumnDefinitionCategory.OTHER],
                sortable: true, 
                sortId: "customValues.valueDate",
                nestedSortFilters: {["customValues.id"]: customField.getId()}
            })
            break
        case CUSTOM_FIELD_TYPE.FILE:
            columnDefinition.push({
                id: customField.getId() as TableRowId,
                dataType: DataType.FILE,
                accessorFn: row=> {
                    const fileCustomField = row?.customValues?.find(
                        customVal => customVal.id === customField.getId() &&
                        customVal.fieldType === CUSTOM_FIELD_TYPE.FILE
                    )
                    if (fileCustomField && fileCustomField.value) {
                        return JSON.parse(fileCustomField?.value)?.fileIds ?? []
                    }
                    return []
                },
                categories: [ColumnDefinitionCategory.OTHER],
                sortable: false, 
            })
            break
        default:
            throw new Error("Error: Invalid content type for custom field.")
        }
    })
    return columnDefinition
}

export const getCustomFieldHeaders = (customFields: CustomField[]): Messages => {    
    const customFieldMessages = customFields.reduce((acc, field) => {
        acc[field.getId()] = {
            id: field.getId(),
            defaultMessage: field.getName(),
        }
        return acc
    }, {} as Messages)
    return customFieldMessages
}


/**
 * Generic are properties present on all column definitions
 */
const mapColumnDefinitionGenerics = (
    columnDefinition: ColumnDefinition,
    headerMessages: Messages,
    totals: SearchTotals | undefined,
    showTotal: boolean,
    customFieldHeaders?: Messages,
): ColumnDef<TableRowData> => ({
    id: columnDefinition.id,
    header: () => {
        if(customFieldHeaders && customFieldHeaders[columnDefinition.id]){
            return customFieldHeaders[columnDefinition.id].defaultMessage
        } 
        return <FormattedMessage {...getValueFromObject(headerMessages, columnDefinition.id)} />
    },
    ...mapColumnDefinitionAccessor(columnDefinition),
    ...mapColumnDefinitionMeta(columnDefinition),
    ...(showTotal ? mapColumnDefinitionTotal(columnDefinition, totals) : {}),
})

/**
 * Accessors define how a cell will access the data it has to render
 *   accessorKey - defines a simple key-value access on the object
 *   accessorFn - defines a complex functional access on the object
 */
const mapColumnDefinitionAccessor = (columnDefinition: ColumnDefinition): Partial<ColumnDef<TableRowData>> => {
    if (columnDefinition.accessorFn) {
        return {
            accessorFn: columnDefinition.accessorFn
        }
    }

    return {
        accessorKey: columnDefinition.accessorKey
            ? columnDefinition.accessorKey
            : columnDefinition.id
    }
}

/**
 * The meta object can be used to map any custom data needed for rendering
 */
const mapColumnDefinitionMeta = (columnDefinition: ColumnDefinition): Partial<ColumnDef<TableRowData>> => ({
    meta: {
        align: columnDefinition.dataAttributes?.textAlign ? columnDefinition.dataAttributes.textAlign : "left",
        sortable: columnDefinition.sortable ?? false,
        columnDefinition,
    }
})

/**
 * Totals are the summed values in a column's footer
 */
const mapColumnDefinitionTotal = (
    columnDefinition: ColumnDefinition,
    totals: SearchTotals | undefined,
): Partial<ColumnDef<TableRowData>> => {
    if (columnDefinition.totalAttributes) {
        switch (columnDefinition.totalAttributes.type) {
        case TotalType.STRING:
            return stringTotalMapper(columnDefinition as StringColumnDefinition, totals)
        case TotalType.CURRENCY:
            return currencyTotalMapper(columnDefinition as CurrencyColumnDefinition, totals)
        default:
            throw new Error("Error: Invalid total type for column definition.")
        }
    }

    return {}
}

export const getTotalsValue = (key: string, totals: SearchTotals | undefined): string => {
    if (totals?.isLoaded()) {
        return getValueFromObject(totals.getAttributes(), key)
    }
    return ""
}


// WEBPACK FOOTER //
// ./src/common/listing/generic-listing/listing-table/mappers/index.tsx